import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Users, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Logo } from '../components/Logo';
import CreateChannelDialog from '../components/CreateChannelDialog';
import PostActions from '../components/PostActions';
import PostAttachment from '../components/PostAttachment';

interface Post {
  id: string;
  content: string;
  created_at: string;
  user: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  channel?: {
    id: string;
    name: string;
  } | null;
  attachments: Array<{
    id: string;
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnail_url?: string;
  }>;
  likes: number;
  comments: number;
  liked_by_me: boolean;
}

interface Channel {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  cover_image: string | null;
}

function Explore() {
  const [activeTab, setActiveTab] = useState<'posts' | 'channels'>('posts');
  const [posts, setPosts] = useState<Post[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateChannel, setShowCreateChannel] = useState(false);

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchPosts();
    } else {
      fetchChannels();
    }
  }, [activeTab]);

  const fetchPosts = async () => {
    try {
      const { data: postsData } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          user:users(id, username, display_name, avatar_url),
          channel:channels(id, name),
          attachments:post_attachments(
            id,
            type,
            url,
            title,
            description,
            thumbnail_url
          ),
          likes:likes(count),
          comments:comments(count)
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      const { data: userLikes } = await supabase
        .from('likes')
        .select('post_id');

      const likedPostIds = new Set(userLikes?.map(like => like.post_id) || []);

      const formattedPosts = postsData?.map(post => ({
        ...post,
        attachments: post.attachments || [],
        likes: post.likes[0]?.count ?? 0,
        comments: post.comments[0]?.count ?? 0,
        liked_by_me: likedPostIds.has(post.id)
      })) || [];

      setPosts(formattedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const { data } = await supabase
        .from('channels')
        .select('*')
        .order('member_count', { ascending: false });

      setChannels(data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="fixed top-0 left-0 right-0 bg-white border-b z-50">
        <div className="h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8 text-indigo-600" />
            <span className="text-lg font-bold text-indigo-600">Explore</span>
          </div>
          
          {activeTab === 'channels' && (
            <button
              onClick={() => setShowCreateChannel(true)}
              className="p-2 text-gray-600 hover:text-indigo-600 transition-colors"
            >
              <Plus className="h-6 w-6" />
            </button>
          )}
        </div>

        <div className="px-4 pb-2">
          <div className="bg-gray-100 p-1 rounded-lg grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveTab('posts')}
              className={`py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'posts'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'hover:bg-white/50 text-gray-600'
              }`}
            >
              Posts
            </button>
            <button
              onClick={() => setActiveTab('channels')}
              className={`py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'channels'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'hover:bg-white/50 text-gray-600'
              }`}
            >
              Channels
            </button>
          </div>
        </div>
      </header>

      <div className="pt-28 pb-20">
        {activeTab === 'posts' ? (
          loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-white p-8 text-center">
              <p className="text-gray-500">No posts yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {posts.map(post => (
                <div key={post.id} className="bg-white">
                  <div className="px-4 py-3">
                    <div className="flex items-start space-x-3">
                      <Link to={`/profile/${post.user.id}`}>
                        <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden">
                          {post.user.avatar_url ? (
                            <img
                              src={post.user.avatar_url}
                              alt={post.user.username}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/profile/${post.user.id}`}
                            className="font-semibold hover:text-indigo-600 transition-colors"
                          >
                            {post.user.display_name || post.user.username}
                          </Link>
                          {post.channel && (
                            <>
                              <span className="text-gray-500">in</span>
                              <Link
                                to={`/channel/${post.channel.name}`}
                                className="text-indigo-600 hover:text-indigo-700 transition-colors"
                              >
                                #{post.channel.name}
                              </Link>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(post.created_at).toLocaleString()}
                        </p>
                        
                        <p className="text-gray-900 mt-2">{post.content}</p>

                        {post.attachments?.map((attachment) => (
                          <PostAttachment
                            key={attachment.id}
                            type={attachment.type}
                            url={attachment.url}
                            title={attachment.title}
                            description={attachment.description}
                            thumbnailUrl={attachment.thumbnail_url}
                          />
                        ))}

                        <PostActions
                          postId={post.id}
                          userId={post.user.id}
                          likes={post.likes}
                          comments={post.comments}
                          liked={post.liked_by_me}
                          onLikeChange={fetchPosts}
                          onDelete={fetchPosts}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="px-4">
            <div className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : channels.length === 0 ? (
                <div className="bg-white p-8 text-center rounded-lg shadow-sm border-2 border-gray-200">
                  <Users className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Channels Yet</h3>
                  <p className="text-gray-600 mb-6">
                    Be the first to create a channel
                  </p>
                  <button
                    onClick={() => setShowCreateChannel(true)}
                    className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Create Channel
                  </button>
                </div>
              ) : (
                channels.map(channel => (
                  <Link
                    key={channel.id}
                    to={`/channel/${channel.name}`}
                    className="block bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-indigo-200 transition-colors overflow-hidden"
                  >
                    {channel.cover_image && (
                      <div className="h-32 bg-gray-100">
                        <img
                          src={channel.cover_image}
                          alt={channel.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">#{channel.name}</h3>
                          {channel.description && (
                            <p className="text-gray-600 mt-1">{channel.description}</p>
                          )}
                        </div>
                        <div className="flex items-center text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          {channel.member_count}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <CreateChannelDialog
        isOpen={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
        onSuccess={fetchChannels}
      />
    </div>
  );
}

export default Explore;